// The phone's mic when iOS mutes its track (another app takes the mic, or a brief interruption
// leaves it muted). A muted track is not a call: the reply keeps playing and nothing is paused
// (./hold.ts pauses only for held audio). The page says "Mic interrupted · Tap to reconnect";
// a tap fetches a fresh mic inside the tap (a new getUserMedia call and audio context,
// VoiceKitClient.reopenMic), at most once per mute episode. While it is on its way the page
// says "Reconnecting the mic…" and further taps in the session don't touch the mic. If the
// fresh track is muted too, another app has the mic: the page says so ("other"). If the
// reconnect is still on its way after RECONNECT_GIVE_UP_MS (getUserMedia has no time limit, and
// iOS may never answer while the mic is held, or just be slow to wake), the page says the mic
// isn't responding ("stuck"), without guessing why. Either way the orb is a voice toggle again
// (a tap ends voice). When iOS unmutes the track by itself, the page is listening again
// straight away. Underneath, the capture doesn't wait for the tap: after MUTE_GRACE_MS it
// fetches a fresh track by itself where iOS allows it (../mic-recovery.ts, with backoff), and
// the label goes as soon as one comes back unmuted; the tap is for when that can't work. This
// module never retries on its own and never reloads.

/**
 * How long "Reconnecting the mic…" may last before the page says "The mic isn't responding".
 * Deliberately shorter than a full wake (MicCapture's WAKE_MS per attempt, plus a retry): the
 * orb shouldn't sit unusable for 12 s or more. A mic that does come up later is used anyway
 * (the reconnect still settles), and the neutral wording doesn't blame another app.
 */
export const RECONNECT_GIVE_UP_MS = 5000;

/** The mic's held state, as the mic view shows it (./mic-state.ts). */
export type MicHeld = "muted" | "other" | "reconnecting" | "stuck" | null;

/** What the orb tap does (`tapAction`). */
export type TapAction =
  | "ignore" // a reconnect of the session's mic is on its way: the tap leaves the mic alone
  | "resume" // carry on the paused reply (and get the mic back as it was)
  | "pause_reply" // a reply is being spoken: pause it, as always
  | "rearm" // a reply is "being spoken" but can't be heard (its output died): bring it and the mic back, no pause
  | "reconnect" // the session's mic is held: fetch a fresh one, in this tap
  | "open_mic" // the session's mic is closed (a typed turn's): open it, in this tap
  | "end_voice" // the orb is a voice toggle: voice ends
  | "start"; // no voice session of the phone's: start one

export interface TapState {
  /** The reply being spoken here is paused. */
  replyPaused: boolean;
  /** A reply is being spoken. */
  speaking: boolean;
  /** The phone runs the session with its replies spoken here (`sessionAudio === "client"`). */
  ours: boolean;
  /** The session's mic is on (`VoiceKitClient.listening`). */
  listening: boolean;
  /** The mic's track is muted (MicCapture.trackMuted). */
  trackMuted: boolean;
  /** The mic has delivered nothing for a while (MicCapture.stalled): not a brief frame gap. */
  micStalled: boolean;
  /** The held state (MicReconnect.held). */
  held: MicHeld;
  /** Stall recovery (MicCapture.recover) is running: the page's own fix comes first. */
  recovering: boolean;
  /** The speaker's audio context isn't running (iOS suspended or interrupted it: after a call). */
  outputDead?: boolean;
}

/**
 * What a tap on the orb does. A reply being spoken is paused and a paused one carried on, as
 * always (whatever the mic does). A held mic is reconnected once per mute episode; once a fresh
 * track came back muted too ("other"), or while stall recovery runs, the tap ends voice as
 * normal. While a reconnect is on its way, taps that would touch the mic are ignored (a second
 * tap would cancel it).
 */
export function tapAction(s: TapState): TapAction {
  if (s.replyPaused) return "resume";
  // Not the phone's voice session (none, a typed one, it ended): start one, whatever was pending.
  if (!s.ours) return "start";
  // A reply that can't be heard (its output is dead after a call): this tap brings it and the
  // mic back and the conversation carries on. Pausing here was the double tap after a call: the
  // first tap paused, only the second re-armed. A reply that can be heard is paused, as always,
  // whatever the mic does (a mic that gave up, another app holding it).
  if (s.speaking && s.outputDead && s.held !== "reconnecting") return "rearm";
  if (s.speaking) return "pause_reply";
  if (s.held === "reconnecting") return "ignore";
  // Another app has the mic, or the reconnect never answered: the orb ends voice.
  if (s.held === "other" || s.held === "stuck") return "end_voice";
  if (!s.listening) return "open_mic";
  const held = s.trackMuted || s.micStalled;
  if (held && !s.recovering) return "reconnect";
  return "end_voice";
}

/**
 * The tap that carries on a paused reply gets the mic back as it was before the pause: a fresh
 * one if it was on and is now closed, muted or not capturing; never a second fetch of one on its
 * way, nor again once a fresh track came back muted ("other").
 */
export function resumeNeedsMic(s: { micWasOn: boolean; ours: boolean; listening: boolean; trackMuted: boolean; micCapturing: boolean; held: MicHeld }): boolean {
  if (!s.micWasOn || !s.ours || s.held === "reconnecting" || s.held === "other" || s.held === "stuck") return false;
  return !s.listening || s.trackMuted || !s.micCapturing;
}

export interface ReconnectClient {
  /** Fetch the mic again in place (or open it): call synchronously inside the tap. */
  reopenMic(): Promise<void>;
}

export interface ReconnectCapture {
  trackMuted(): boolean;
}

/** The session as the held state sees it (VoiceKitClient). */
export interface ReconnectSession {
  readonly listening: boolean;
  readonly ownsSession: boolean;
  readonly sessionAudio: string | null;
}

/** "stale": settled after reset() (voice ended, a new session): nothing for the page to do. */
export type ReconnectResult = "ok" | "held_by_other" | "stale";

export class MicReconnect {
  /** A fresh track came back muted: another app holds the mic. Cleared by an unmute or reset. */
  private other = false;
  /** When the reconnect on its way started (null: none). */
  private pendingSince: number | null = null;
  /** Bumped by reset(): a reconnect of an earlier generation settles as "stale". */
  private gen = 0;
  private why = "";
  private oldTrackMuted = false;

  constructor(
    private readonly capture: ReconnectCapture,
    /** Best-effort diagnostics for the PC's log (`client.log`). */
    private readonly report: (event: string, data: Record<string, unknown>) => void = () => {},
    private readonly now: () => number = () => performance.now(),
  ) {}

  /**
   * What the mic view shows about a held mic (see mic-state.ts); null unless the phone runs the
   * voice session. A reconnect still on its way after RECONNECT_GIVE_UP_MS is "stuck".
   */
  held(c: ReconnectSession | null): MicHeld {
    if (!c?.ownsSession || c.sessionAudio !== "client") return null;
    if (this.pendingSince !== null) return this.now() - this.pendingSince < RECONNECT_GIVE_UP_MS ? "reconnecting" : "stuck";
    if (!c.listening || !this.capture.trackMuted()) return null;
    return this.other ? "other" : "muted";
  }

  /**
   * Fetch a fresh mic, synchronously inside the tap (reopenMic asks for it before awaiting).
   * Resolves "held_by_other" when the new track is muted too, "stale" when reset() came first;
   * rejects as reopenMic does.
   */
  reacquire(c: ReconnectClient, why: string): Promise<ReconnectResult> {
    const oldTrackMuted = this.capture.trackMuted();
    const gen = ++this.gen;
    this.other = false;
    this.pendingSince = this.now();
    this.why = why;
    this.oldTrackMuted = oldTrackMuted;
    let p: Promise<void>;
    try {
      p = c.reopenMic();
    } catch (e) {
      p = Promise.reject(e);
    }
    return p.then(
      () => {
        if (gen !== this.gen) return "stale";
        this.pendingSince = null;
        const newTrackMuted = this.capture.trackMuted();
        const result: ReconnectResult = newTrackMuted ? "held_by_other" : "ok";
        this.report("mic_reacquire", { why, oldTrackMuted, newTrackMuted, result });
        if (newTrackMuted) {
          this.other = true;
          this.report("mic_held_by_other", { why });
        }
        return result;
      },
      (e: unknown) => {
        if (gen !== this.gen) return "stale";
        this.pendingSince = null;
        this.report("mic_reacquire", { why, oldTrackMuted, newTrackMuted: null, result: "failed", error: (e as Error)?.message ?? String(e) });
        throw e;
      },
    );
  }

  /** iOS unmuted the track by itself: the mic is back, listening again. */
  unmuted(): void {
    this.other = false;
  }

  /**
   * Voice ended, the session ended, or a new one starts: a new mute episode from scratch. A
   * reconnect still on its way is dropped (it settles as "stale").
   */
  reset(): void {
    if (this.pendingSince !== null) {
      const waitedMs = Math.round(this.now() - this.pendingSince);
      const result = waitedMs >= RECONNECT_GIVE_UP_MS ? "timeout" : "dropped";
      this.report("mic_reacquire", { why: this.why, oldTrackMuted: this.oldTrackMuted, newTrackMuted: null, result, waitedMs });
    }
    this.gen++;
    this.pendingSince = null;
    this.other = false;
  }
}
